import type { ServiceManager } from '@/framework/services/service-manager';
import { setLayerRuntimeValue } from '@/utils/layer-kit';
import { getFontFamilyWithFallback, getPlainTextFontByPretext } from '@/utils/text-kit';
import type { ITableLayer } from '@/version-types';
import { collectTableLayerFontRuns, collectTableLayerFonts } from './utils/table';

type TableFontServices = Pick<ServiceManager, 'font'>;

/**
 * 尽力把表格字体子集注册到 `document.fonts`；失败的字体由原字体和系统字体继续渲染。
 *
 * @param services - 提供字体查询与子集加载能力的服务集合
 * @param layer - 需要准备字体的表格图层
 * @returns 所有字体加载尝试完成后 resolve
 */
export const prepareTableLayerDocumentFonts = async (
    services: TableFontServices,
    layer: ITableLayer,
): Promise<void> => {
    const fontRuns = collectTableLayerFontRuns(layer.data);
    if (!fontRuns.length) return;

    const fontItems = collectTableLayerFonts(layer.data);
    await Promise.allSettled(
        fontItems.map(async ({ fontFamily, text }) => {
            if (!services.font.getByFamily(fontFamily)) return;

            const subsetFamily = await services.font.loadSubset(fontFamily, text, { registerTo: 'document' });
            if (!subsetFamily) return;

            // Canvas 测量仍通过图层映射读取实际注册到 document 的字体族。
            setLayerRuntimeValue(layer, {
                fontFamilyMap: {
                    ...(layer.__runtime?.fontFamilyMap || {}),
                    [fontFamily]: subsetFamily,
                },
            });
        }),
    );
    if (typeof document === 'undefined' || !document.fonts) return;

    await Promise.allSettled(
        fontRuns.map(async ({ fontFamily, fontSize, bold, italic, text }) => {
            const subsetFamily = layer.__runtime?.fontFamilyMap?.[fontFamily];
            const font = getPlainTextFontByPretext({
                fontFamily: getFontFamilyWithFallback(fontFamily, subsetFamily),
                fontSize,
                bold,
                italic,
            });
            await document.fonts.load(font, text);
        }),
    );
};
